import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
// Constants
import {
  LocationCity,
  LocationCountry,
  MainTitle,
  SubtitleMain,
} from '../../utils/Constants';
import {
  COMMERCIALS_PAGE_URL,
  CONTACT_PAGE_URL,
  WELLNESS_PAGE_URL,
} from '../../utils/Routes';
// Video
import SampleVideo720 from '../../assets/video/commercial-sample-video-siv-renate-720.mp4';
import SampleVideo1080 from '../../assets/video/commercial-sample-video-siv-renate-1080.mp4';
import Poster from '../../assets/images/posters/commercial-sample-video-siv-renate-poster.webp';
// Icons
import { MdDoubleArrow } from 'react-icons/md';

function CommercialsPageHeader() {
  const phoneVideoRef = useRef(null);
  const desktopVideoRef = useRef(null);

  useEffect(() => {
    const vids = [phoneVideoRef.current, desktopVideoRef.current].filter(
      Boolean
    );

    const tryPlay = (video) => {
      if (!video) return;
      const p = video.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    // Start ASAP on ready
    const canHandlers = vids.map((v) => {
      const h = () => tryPlay(v);
      v?.addEventListener('canplay', h, { once: true });
      return { v, h, type: 'canplay' };
    });
    const loadedHandlers = vids.map((v) => {
      const h = () => tryPlay(v);
      v?.addEventListener('loadeddata', h, { once: true });
      return { v, h, type: 'loadeddata' };
    });

    // Pause when out of view; resume when back
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const video = e.target;
          if (e.isIntersecting) tryPlay(video);
          else video.pause();
        });
      },
      { rootMargin: '100px 0px 100px 0px', threshold: 0.2 }
    );
    vids.forEach((v) => v && io.observe(v));

    // Pause when tab hidden; resume if visible and in view
    const onVis = () => {
      vids.forEach((v) => {
        if (!v) return;
        if (document.hidden) {
          v.pause();
          return;
        }
        const r = v.getBoundingClientRect();
        const inView =
          r.bottom >= 1 &&
          r.right >= 1 &&
          r.top <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          r.left <= (window.innerWidth || document.documentElement.clientWidth);
        if (inView) tryPlay(v);
      });
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      canHandlers.forEach(({ v, h, type }) => v?.removeEventListener(type, h));
      loadedHandlers.forEach(({ v, h, type }) =>
        v?.removeEventListener(type, h)
      );
      io.disconnect();
    };
  }, []);

  return (
    <header className='grid min-h-screen pt-[102px] lg:pt-[84px] w-full h-full overflow-x-hidden'>
      {/* Phone */}
      <section className='grid lg:hidden px-8 py-4 w-full'>
        <article className='grid justify-center gap-y-8'>
          <div className='grid h-fit gap-y-8 text-center'>
            <h1
              className='font-titleFont text-4xl w-full text-colour5 tracking-wide'
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.6)' }}
            >
              Commercials
            </h1>
            <div className='text-base font-titleFont text-center px-8'>
              <h2>{MainTitle}</h2>
            </div>
          </div>

          <div className='grid justify-center'>
            <video
              ref={phoneVideoRef}
              autoPlay
              loop
              muted
              playsInline
              preload='auto'
              poster={Poster}
              disablePictureInPicture
              controlsList='nodownload noremoteplayback nofullscreen'
              className='w-full h-full object-cover rounded-lg border_special'
            >
              <source src={SampleVideo720} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className='text-center text-lg'>
            <p>
              Crafting <span className='font-semibold'>commercials</span> and{' '}
              <span className='font-semibold'>UGC content</span> that tell real
              stories, blending creativity and professional media editing to
              deliver impactful results for brands and clients worldwide.
            </p>
          </div>

          <div className='grid h-fit px-8 text-center'>
            <Link
              to={CONTACT_PAGE_URL}
              aria-label={`Contact me`}
              className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
            >
              <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4'>
                <span>CONTACT ME NOW</span>
                <MdDoubleArrow className='text-xl' />
              </div>
            </Link>
          </div>

          {/* Cta */}
          <section className='grid grid-cols-3 gap-x-2 w-full justify-between text-center'>
            <Link
              to={WELLNESS_PAGE_URL}
              className='text-colour5 underline hover:brightness-75 text-center'
              aria-label='Learn more about wellness coaching with Siv Renate'
              title='Wellness coaching by Siv Renate — available worldwide'
            >
              Wellness
            </Link>

            <Link
              to={COMMERCIALS_PAGE_URL}
              className='text-colour5 underline hover:brightness-75 text-center'
              aria-label='Explore Siv Renate’s commercials and UGC portfolio'
              title='Commercials and UGC portfolio by Siv Renate'
            >
              Commercials
            </Link>

            <Link
              to={CONTACT_PAGE_URL}
              className='text-colour5 underline hover:brightness-75 text-center'
              aria-label='Get in touch with Siv Renate'
              title='Contact Siv Renate for inquiries, bookings, or collaborations'
            >
              Contact
            </Link>
          </section>
        </article>
      </section>

      {/* Large screen */}
      <section className='hidden relative lg:grid w-full'>
        {/* LEFT rail */}
        <div className='absolute inset-y-0 left-0 w-12 z-20 grid place-items-center'>
          <span className='[writing-mode:vertical-rl] rotate-180 [text-orientation:mixed] underline underline-offset-4'>
            From {LocationCity}, {LocationCountry}
          </span>
        </div>

        {/* RIGHT rail */}
        <div className='absolute inset-y-0 right-0 w-12 z-20 grid place-items-center'>
          <span className='[writing-mode:vertical-rl] rotate-180 [text-orientation:mixed] underline underline-offset-4'>
            {SubtitleMain}
          </span>
        </div>

        <div className='absolute grid w-full h-full items-center transform top-1/2 -translate-y-1/2 px-44'>
          <video
            ref={desktopVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload='auto'
            poster={Poster}
            className='w-full h-auto object-cover max-h-[60vh]'
          >
            <source src={SampleVideo1080} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>

        <article className='grid w-full h-full pt-16 items-center z-20'>
          <section className='relative px-12 pl-20 grid items-center'>
            <div className='hidden lg:block text-2xl font-titleFont pb-4 pl-44 text-colour1 text-left'>
              <h2
                className='text-sm'
                style={{ textShadow: '4px 4px 6px rgba(0,0,0,0.9)' }}
              >
                {MainTitle}
              </h2>
            </div>
            <h1
              className='font-agraham text-[5.5rem] 2xl:text-[8rem] w-full text-colour5 tracking-wide'
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.6)' }}
            >
              Commercials
            </h1>
            <div className='grid h-fit grid-cols-rev pr-8 pt-8 pl-44'>
              <div className='grid items-center text-center text-colour1'>
                <p
                  className='text-sm xl:text-lg max-w-[50vw]'
                  style={{ textShadow: '4px 4px 6px rgba(0,0,0,0.9)' }}
                >
                  My passion is creating{' '}
                  <span className='font-semibold'>commercials</span>
                  and <span className='font-semibold'>UGC content</span> that
                  inspire. Through authentic storytelling and polished editing,
                  I help brands share their message with clarity and impact.
                </p>
              </div>
            </div>
            <div className='grid h-fit px-8 text-center justify-start pt-12'>
              <Link
                to={CONTACT_PAGE_URL}
                aria-label={`Contact me`}
                className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
              >
                <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4'>
                  <span>CONTACT ME NOW</span>
                  <MdDoubleArrow className='text-xl' />
                </div>
              </Link>
            </div>
          </section>
        </article>
      </section>
    </header>
  );
}

export default CommercialsPageHeader;
